import { sortBy } from 'lodash';
import moment from 'moment';
import { INTER_MODAL_TYPE_EQUIPMENT_TYPES, MARGIN_TYPE, QUOTE_CLASSIFICATION, QUOTE_URGENCY } from 'src/constants/common';
import { formatDecimalNumber, normalizeToOneDigitPrecision, formatNumberWithRoundOff, getSelectBoxOptions } from 'src/utils/CommonFunctions';

import { IAddressForm } from './Modals/quoteDetailModal.interface';

export const getBusinessDays = (startDate: any, endDate: any, pickupDate?: any) => {
    let count = 0;

    const start = moment(startDate).startOf('day');
    const end = moment(endDate).startOf('day');
    const pickup = pickupDate ? moment(pickupDate).startOf('day') : null;

    const current = moment(start).startOf('day');

    while (current.isSameOrBefore(end, 'day')) {
        const dayOfWeek = current.day();

        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            if (!(pickup && current.isSame(pickup, 'day'))) {
                count++;
            }
        }

        current.add(1, 'days');
    }

    return count;
};

export const getDeliveryDateFromBusinessDays = (startDate: any, businessDays: number, skipStartDate: boolean = true) => {
    if (businessDays === 0) {
        return moment(startDate).startOf('day');
    }

    const start = moment(startDate).startOf('day');
    const deliveryDate = skipStartDate ? moment(start).add(1, 'days') : moment(start);

    let targetDays = businessDays + 1;
    let daysAdded = 0;

    while (daysAdded < targetDays) {
        const dayOfWeek = deliveryDate.day();

        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            daysAdded++;
        }

        if (daysAdded < targetDays) {
            deliveryDate.add(1, 'days');
        }
    }

    return deliveryDate;
};

export const isValidServices = (formData: any, setValidForm: any) => {
    const services: any = [];
    formData?.services.map((item: any) => {
        let itemKey = Object.keys(item);

        services.push({
            label: item?.[itemKey?.[0]]?.name && item?.[itemKey?.[0]]?.name !== '' ? true : false,
            price: true,
        });
    });

    setValidForm((prevValidForm: any) => ({
        ...prevValidForm,
        services: services,
    }));

    const isValidFields = services?.every((item: any) => item.label);

    return isValidFields;
};

let prevExistingFormData: any = undefined;

export const calculateMarginAndTotalCommonFunction = (name: string, existingFormData: any, activeMarginType: any, isService = false) => {
    if (['totalCharge', 'marginUnit', 'margin', 'finalCharge'].includes(name) || isService) {
        if (isService) {
            let updatedAdditionCharge = 0;
            existingFormData?.services?.forEach((quoteService: any) => {
                Object.entries(quoteService).forEach(([, service]: any) => {
                    service.value = service.value || 0;
                    updatedAdditionCharge += formatDecimalNumber(service.value);
                });
            });
            existingFormData.additionalCharges = formatDecimalNumber(updatedAdditionCharge);
        }

        if (!existingFormData.additionalCharges) {
            existingFormData.additionalCharges = 0;
        }

        existingFormData.totalCharge = formatNumberWithRoundOff(existingFormData.totalCharge);
        existingFormData.marginUnit = formatNumberWithRoundOff(existingFormData.marginUnit);
        existingFormData.margin = formatNumberWithRoundOff(existingFormData.margin);
        existingFormData.finalCharge = formatNumberWithRoundOff(existingFormData.finalCharge);

        const total: number = formatDecimalNumber(existingFormData.totalCharge || 0) + formatDecimalNumber(existingFormData.additionalCharges || 0);

        existingFormData.marginType = activeMarginType === '$' ? MARGIN_TYPE.VALUE : MARGIN_TYPE.PERCENTAGE;

        let finalCharge = existingFormData.finalCharge ? formatDecimalNumber(existingFormData.finalCharge) : 0;

        let margin: number = existingFormData.margin ? formatDecimalNumber(existingFormData.margin) : 0;

        let marginUnit: number = existingFormData.marginUnit ? Number(existingFormData.marginUnit) : 0;

        let nonEmptyFields = [total === 0 ? (existingFormData.additionalCharges && existingFormData.totalCharge ? 1 : 0) : total, finalCharge, margin, marginUnit].filter(
            (val) => val !== undefined && val !== 0 && !isNaN(val) && val !== Infinity
        ).length;

        if (nonEmptyFields < 2) {
            return existingFormData;
        }

        if (
            prevExistingFormData &&
            prevExistingFormData?.additionalCharges === existingFormData.additionalCharges &&
            prevExistingFormData?.totalCharge === existingFormData.totalCharge &&
            prevExistingFormData?.margin === existingFormData.margin &&
            prevExistingFormData?.marginUnit === existingFormData.marginUnit &&
            prevExistingFormData?.finalCharge === existingFormData.finalCharge
        ) {
            return existingFormData;
        }

        const calculateMarginUnit = (marginValue: number, finalChargeValue: number) => {
            const calculateValue = (marginValue * 100) / finalChargeValue;

            return finalChargeValue !== 0
                ? (calculateValue > 0 && calculateValue > 0.001) || (calculateValue < 0 && calculateValue < -0.01)
                    ? formatNumberWithRoundOff(calculateValue)
                    : normalizeToOneDigitPrecision(calculateValue)
                : 0;
        };

        if (existingFormData.isUnassigned || existingFormData.isPrivateCarrier || !existingFormData.carrierId) {
            if (name === 'totalCharge' || name === 'finalCharge') {
                existingFormData.margin = formatDecimalNumber(existingFormData.finalCharge - existingFormData.totalCharge - existingFormData.additionalCharges);

                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);

                return existingFormData;
            } else if ((name === 'margin' || name === 'marginUnit') && existingFormData.totalCharge && existingFormData.finalCharge) {
                if (name === 'marginUnit') {
                    existingFormData.margin = formatDecimalNumber((existingFormData.finalCharge * existingFormData.marginUnit) / 100);
                } else {
                    existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
                }

                existingFormData.totalCharge = formatDecimalNumber(existingFormData.finalCharge - existingFormData.margin - existingFormData.additionalCharges);

                return existingFormData;
            }
        }

        if (name === 'totalCharge' || isService) {
            if (!existingFormData.additionalCharges && !existingFormData.totalCharge && existingFormData.margin && existingFormData.finalCharge) {
                existingFormData.totalCharge = formatDecimalNumber(existingFormData.finalCharge - existingFormData.margin - existingFormData.additionalCharges);
            } else if (activeMarginType === '$' && existingFormData.margin && existingFormData.finalCharge && (existingFormData.additionalCharges || existingFormData.totalCharge)) {
                existingFormData.finalCharge = formatDecimalNumber(existingFormData.totalCharge + existingFormData.margin + existingFormData.additionalCharges);

                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
            } else if (activeMarginType === '%' && existingFormData.marginUnit && existingFormData.finalCharge && (existingFormData.additionalCharges || existingFormData.totalCharge)) {
                if (Number(existingFormData.marginUnit) === 100) {
                    existingFormData.margin = formatDecimalNumber(existingFormData.totalCharge + existingFormData.additionalCharges);

                    existingFormData.finalCharge = formatDecimalNumber(existingFormData.totalCharge + existingFormData.additionalCharges);
                    existingFormData.totalCharge = formatDecimalNumber(0 - existingFormData.additionalCharges);
                } else if (Number(existingFormData.marginUnit) > 100) {
                    existingFormData.margin = formatDecimalNumber(existingFormData.finalCharge - existingFormData.totalCharge - existingFormData.additionalCharges);
                    existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
                } else {
                    existingFormData.finalCharge = formatDecimalNumber((100 * (existingFormData.totalCharge + existingFormData.additionalCharges)) / (100 - existingFormData.marginUnit));

                    existingFormData.margin = formatDecimalNumber(existingFormData.finalCharge - (existingFormData.totalCharge + existingFormData.additionalCharges));
                }
            } else if (activeMarginType === '$' && existingFormData.margin) {
                existingFormData.finalCharge =
                    existingFormData.totalCharge + existingFormData.additionalCharges + existingFormData.margin
                        ? formatDecimalNumber(existingFormData.totalCharge + existingFormData.additionalCharges + existingFormData.margin)
                        : 0;

                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
            } else if (activeMarginType === '%' && existingFormData.marginUnit) {
                if (Number(existingFormData.marginUnit) === 100) {
                    existingFormData.margin = formatDecimalNumber(existingFormData.totalCharge + existingFormData.additionalCharges);

                    existingFormData.finalCharge = formatDecimalNumber(existingFormData.totalCharge + existingFormData.additionalCharges);
                    existingFormData.totalCharge = formatDecimalNumber(0 - existingFormData.additionalCharges);
                } else if (Number(existingFormData.marginUnit) > 100) {
                    existingFormData.marginUnit = 0;
                    existingFormData.margin = 0;
                    existingFormData.finalCharge = 0;
                } else {
                    existingFormData.finalCharge =
                        existingFormData.totalCharge + existingFormData.additionalCharges
                            ? formatDecimalNumber((100 * (existingFormData.totalCharge + existingFormData.additionalCharges)) / (100 - existingFormData.marginUnit))
                            : 0;
                    existingFormData.margin = existingFormData.finalCharge
                        ? formatDecimalNumber(existingFormData.finalCharge - (existingFormData.totalCharge + existingFormData.additionalCharges))
                        : 0;
                }
            } else if (existingFormData.finalCharge && existingFormData.finalCharge > 0 && (!existingFormData.margin || !existingFormData.marginUnit)) {
                existingFormData.margin = formatDecimalNumber(existingFormData.finalCharge - (existingFormData.totalCharge + existingFormData.additionalCharges));
                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
            }
        } else if (activeMarginType === '%' && name === 'marginUnit') {
            if (!existingFormData.marginUnit && existingFormData.finalCharge && existingFormData.margin) {
                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
            } else if (existingFormData.marginUnit && existingFormData.finalCharge && (existingFormData.additionalCharges || existingFormData.totalCharge)) {
                if (Number(existingFormData.marginUnit) === 100) {
                    existingFormData.margin = formatDecimalNumber(existingFormData.totalCharge + existingFormData.additionalCharges);

                    existingFormData.finalCharge = formatDecimalNumber(existingFormData.totalCharge + existingFormData.additionalCharges);
                    existingFormData.totalCharge = formatDecimalNumber(0 - existingFormData.additionalCharges);
                } else if (Number(existingFormData.marginUnit) > 100) {
                    existingFormData.margin = formatDecimalNumber((existingFormData.finalCharge * existingFormData.marginUnit) / 100);
                    existingFormData.totalCharge = formatDecimalNumber(existingFormData.finalCharge - existingFormData.margin - existingFormData.additionalCharges);
                } else {
                    existingFormData.finalCharge = formatDecimalNumber((100 * (existingFormData.totalCharge + existingFormData.additionalCharges)) / (100 - existingFormData.marginUnit));

                    existingFormData.margin = formatDecimalNumber(existingFormData.finalCharge - (existingFormData.totalCharge + existingFormData.additionalCharges));
                }
            } else if (existingFormData.finalCharge) {
                existingFormData.margin = formatDecimalNumber((existingFormData.finalCharge * existingFormData.marginUnit) / 100);
                existingFormData.totalCharge = formatDecimalNumber(existingFormData.finalCharge - existingFormData.margin - existingFormData.additionalCharges);
            } else if (existingFormData.additionalCharges || existingFormData.totalCharge) {
                if (Number(existingFormData.marginUnit) === 100) {
                    existingFormData.margin = formatDecimalNumber(existingFormData.totalCharge + existingFormData.additionalCharges);

                    existingFormData.finalCharge = formatDecimalNumber(existingFormData.totalCharge + existingFormData.additionalCharges);
                    existingFormData.totalCharge = formatDecimalNumber(0 - existingFormData.additionalCharges);
                } else if (Number(existingFormData.marginUnit) > 100) {
                    existingFormData.marginUnit = 0;
                    existingFormData.margin = 0;
                    existingFormData.finalCharge = 0;
                } else {
                    existingFormData.finalCharge = formatDecimalNumber((100 * (existingFormData.totalCharge + existingFormData.additionalCharges)) / (100 - existingFormData.marginUnit));
                    existingFormData.margin = formatDecimalNumber((existingFormData.finalCharge * existingFormData.marginUnit) / 100);
                }
            }
        } else if (activeMarginType === '$' && name === 'margin') {
            if (!existingFormData.margin && existingFormData.finalCharge && (existingFormData.additionalCharges || existingFormData.totalCharge)) {
                existingFormData.margin = formatDecimalNumber(existingFormData.finalCharge - (existingFormData.totalCharge + existingFormData.additionalCharges));
            } else if (existingFormData.margin && existingFormData.finalCharge && (existingFormData.additionalCharges || existingFormData.totalCharge)) {
                existingFormData.finalCharge = formatDecimalNumber(existingFormData.totalCharge + existingFormData.margin + existingFormData.additionalCharges);

                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
            } else if (existingFormData.finalCharge) {
                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
                existingFormData.totalCharge = formatDecimalNumber(existingFormData.finalCharge - existingFormData.margin - existingFormData.additionalCharges);
            } else if (existingFormData.additionalCharges || existingFormData.totalCharge) {
                existingFormData.finalCharge = formatDecimalNumber(existingFormData.totalCharge + existingFormData.margin + existingFormData.additionalCharges);
                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
            }
        } else if (name === 'finalCharge') {
            if (!existingFormData.finalCharge && existingFormData.margin && (existingFormData.additionalCharges || existingFormData.totalCharge)) {
                existingFormData.finalCharge = formatDecimalNumber(existingFormData.margin + existingFormData.totalCharge + existingFormData.additionalCharges);
            } else if (activeMarginType === '$' && existingFormData.margin && existingFormData.finalCharge && (existingFormData.additionalCharges || existingFormData.totalCharge)) {
                existingFormData.margin = formatDecimalNumber(existingFormData.finalCharge - (existingFormData.totalCharge + existingFormData.additionalCharges));

                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
            } else if (activeMarginType === '%' && existingFormData.marginUnit && existingFormData.finalCharge && (existingFormData.additionalCharges || existingFormData.totalCharge)) {
                if (Number(existingFormData.marginUnit) > 100) {
                    existingFormData.margin = formatDecimalNumber(existingFormData.finalCharge - existingFormData.totalCharge - existingFormData.additionalCharges);
                    existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
                } else {
                    existingFormData.margin = formatDecimalNumber(existingFormData.finalCharge - existingFormData.totalCharge - existingFormData.additionalCharges);
                    existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
                }
            } else if (activeMarginType === '$' && existingFormData.margin) {
                existingFormData.totalCharge = formatDecimalNumber(existingFormData.finalCharge - existingFormData.margin - existingFormData.additionalCharges);

                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
            } else if (activeMarginType === '%' && existingFormData.marginUnit) {
                existingFormData.margin = formatDecimalNumber((existingFormData.finalCharge * existingFormData.marginUnit) / 100);

                existingFormData.totalCharge = formatDecimalNumber(existingFormData.finalCharge - existingFormData.margin - existingFormData.additionalCharges);
            } else if (existingFormData.totalCharge || existingFormData.additionalCharges) {
                existingFormData.margin = formatDecimalNumber(existingFormData.finalCharge - existingFormData.totalCharge - existingFormData.additionalCharges);
                existingFormData.marginUnit = calculateMarginUnit(existingFormData.margin, existingFormData.finalCharge);
            }
        }
    }

    // Create a deep copy of the current state before storing it
    prevExistingFormData = JSON.parse(JSON.stringify(existingFormData));

    return existingFormData;
};

const isBusinessDay = (date: moment.Moment) => {
    const day = date.day();

    return day !== 0 && day !== 6; // Skip weekends
};

export const calculateBusinessDays = (startDate: any, endDateOrBusinessDays: any, options?: { pickupDate?: any; skipStartDate?: boolean }) => {
    let current = moment(moment(startDate).format('YYYY-MM-DD')).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
    });
    let count = 0;

    const pickupDate = options?.pickupDate ? moment(options.pickupDate).startOf('day') : null;
    const skipStartDate = options?.skipStartDate || false;

    if (typeof endDateOrBusinessDays === 'number') {
        let businessDays = endDateOrBusinessDays;

        if (businessDays === 0) {
            return current;
        }

        if (skipStartDate) {
            current.add(1, 'days');
        }

        while (businessDays > 0) {
            if (isBusinessDay(current) && (!pickupDate || !current.isSame(pickupDate, 'day'))) {
                businessDays--;
            }

            if (businessDays > 0) {
                current.add(1, 'days');
            }
        }

        return current;
    }

    if (moment.isMoment(moment(endDateOrBusinessDays))) {
        const end = moment(endDateOrBusinessDays).set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        });

        if (skipStartDate) {
            current.add(1, 'days');
        }

        while (
            current.year() < end.year() ||
            (current.year() === end.year() && current.month() < end.month()) ||
            (current.year() === end.year() && current.month() === end.month() && current.date() <= end.date())
        ) {
            if (isBusinessDay(current) && (!pickupDate || !current.isSame(pickupDate, 'day'))) {
                count++;
            }
            current.add(1, 'days');
        }

        return count === 0 ? 1 : count;
    }

    return current;
};

export const normalizeServicesData = (servicesData: any = []) => {
    const result = {
        serviceId: servicesData.map((s: any) => s.serviceId),
        limitedAccess: false,
        inbond: false,
        dangerousGoods: false,
        isDeclaredValue: false,
        servicesData,
    };

    for (const service of servicesData) {
        switch (service?.slug) {
            case 'limited_access_delivery':
                Object.assign(result, {
                    limitedAccess: true,
                    limitedAccessType: service.limitedAccessType || '',
                    limitedAccessOther: service.limitedAccessOther || '',
                });
                break;
            case 'in-bond':
                Object.assign(result, {
                    inbond: true,
                    inbondType: service.inbondType || '',
                    inbondName: service.inbondName || '',
                    inbondAddress: service.inbondAddress || '',
                    inbondContactMethod: service.inbondContactMethod || '',
                    inbondContactValue: service.inbondContactValue || '',
                });
                break;
            case 'dangerous_goods':
                Object.assign(result, {
                    dangerousGoods: true,
                    dangerGoodsType: service.dangerGoodsType || '',
                    goodsClass: service.goodsClass || '',
                    description: service.description || '',
                    unitedNationsNumber: service.unitedNationsNumber || '',
                    packagingGroup: service.packagingGroup || '',
                    emergencyContactName: service.emergencyContactName || '',
                    emergencyContactNumber: service.emergencyContactNumber || '',
                    emergencyContactExt: service.emergencyContactExt || '',
                });
                break;
            case 'excess_valuation':
                Object.assign(result, {
                    isDeclaredValue: true,
                    currency: service.currency || '',
                    declaredValue: service.declaredValue || 0,
                });
                break;
        }
    }

    return result;
};

export const setTeamMemberOptions = (team: any, userSelected: any, teamMembers: any[]) => {
    teamMembers = sortBy(teamMembers, ['firstName', 'lastName']);

    if (team === 'all' && userSelected != 'all') {
        const selectedTeamMemberIndex = teamMembers.findIndex((member: any, index: number) => member.id == userSelected && index > 4);

        if (selectedTeamMemberIndex !== -1) {
            const selectedTeamMember = teamMembers[selectedTeamMemberIndex];
            teamMembers = teamMembers.slice(0, 4);
            teamMembers.unshift(selectedTeamMember);
        } else {
            teamMembers = teamMembers.slice(0, 5);
        }
    } else {
        teamMembers = teamMembers.slice(0, 5);
    }

    return getSelectBoxOptions(teamMembers, 'id', 'firstName', true, 'imageUrl', 'image', undefined, 'lastName', true, null, 'email');
};

export const isParamsChanged = (obj1: any, obj2: any) => {
    const fieldsInFilter = ['status', 'classification'];

    if (fieldsInFilter.some((field) => obj1?.appliedFilters?.[field] !== obj2?.[field])) {
        return true;
    }

    const fieldsTopLevel = ['team', 'member', 'startDate', 'endDate', 'search'];

    if (
        fieldsTopLevel.some((field) => {
            const val1 = obj1?.appliedFilters?.[field];
            const val2 = obj2?.[field];

            if (field === 'startDate' || field === 'endDate') {
                const normalizedVal1 = val1 === '' || val1 === null ? null : val1;
                const normalizedVal2 = val2 === '' || val2 === null ? null : val2;

                return normalizedVal1 !== normalizedVal2;
            }

            return val1 !== val2;
        })
    ) {
        return true;
    }

    const fieldsInSorting = ['sortType', 'sortField'];

    if (fieldsInSorting.some((field) => obj1?.appliedSorting?.[field] !== obj2?.[field])) {
        return true;
    }

    return false;
};

export const formateTabData = (payloadArray: any[]) =>
    payloadArray.map((item) => ({
        name: item.name,
        value: item.id,
        id: item?.id,
        appliedFilters: item.appliedFilters,
        appliedSorting: item.appliedSorting,
        isIconShow: true,
    }));

export const extractViewNames = (tabsArray: any[]) => {
    const names = tabsArray.map((tab: any) => tab?.name).filter(Boolean);

    return Array.from(new Set(names));
};

export const addActiveQuoteTag = (carriers: any[]) => {
    if (!carriers?.length) return carriers;

    const publicCarriers = carriers;

    const publicCarriersSortedByPrice = [...publicCarriers].sort((a, b) => {
        const chargeA = a?.finalChargeUSD ?? a?.finalCharge ?? 0;
        const chargeB = b?.finalChargeUSD ?? b?.finalCharge ?? 0;

        return chargeA - chargeB;
    });

    const cheapestCarriers = publicCarriersSortedByPrice
        .filter((carrier) => (publicCarriersSortedByPrice[0]?.finalChargeUSD ?? publicCarriersSortedByPrice[0]?.finalCharge) === (carrier?.finalChargeUSD ?? carrier?.finalCharge))
        .sort((a, b) => {
            const timeA = a.transitTime ?? 0;
            const timeB = b.transitTime ?? 0;

            return timeA - timeB;
        });

    const cheapestCarrierIds: number[] = [];

    cheapestCarriers.forEach((carrier: any) => {
        if (cheapestCarriers[0]?.transitTime === carrier?.transitTime && cheapestCarrierIds.length === 0) {
            cheapestCarrierIds.push(carrier.id);
        }
    });

    const fastestCarriers = publicCarriersSortedByPrice
        .filter((carrier) => !cheapestCarrierIds.includes(carrier.id))
        .sort((a, b) => {
            const timeA = a.transitTime ?? 0;
            const timeB = b.transitTime ?? 0;

            return timeA - timeB;
        });

    const expressCarrierIds: number[] = [];

    fastestCarriers.forEach((carrier: any) => {
        if ((fastestCarriers[0]?.finalChargeUSD ?? fastestCarriers[0]?.finalCharge) === (carrier?.finalChargeUSD ?? carrier?.finalCharge) && expressCarrierIds.length === 0) {
            expressCarrierIds.push(carrier.id);
        }
    });

    return carriers.map((carrier) => ({
        ...carrier,
        tag: cheapestCarrierIds.includes(carrier.id) ? 'Cheapest' : expressCarrierIds.includes(carrier.id) ? 'Fastest' : '-',
    }));
};

export const sortDataByIsQuoted = (data: any) =>
    data.sort((a: any, b: any) => {
        const aIsQuotedFalse = a?.services.some((service: any) => service.isQuoted === false);
        const bIsQuotedFalse = b?.services.some((service: any) => service.isQuoted === false);

        return aIsQuotedFalse === bIsQuotedFalse ? 0 : aIsQuotedFalse ? 1 : -1;
    });

export const createPayloadFromAddressForm = (data: IAddressForm) => {
    const payload = {
        address: {
            urgency: QUOTE_URGENCY.LIVE,
            pickupDate: moment.utc(data.pickupDate).format('YYYY-MM-DD 00:00:00'),
            pickupSensitivity: data.pickupSensitivity,
            pickupTimeFrame: data.pickupTimeFrame,
            pickupEndDate: data.pickupEndDate ? moment.utc(data.pickupEndDate).format('YYYY-MM-DD 00:00:00') : null,
            pickupWindowType: data.pickupWindowType,
            pickupStartTime: data.pickupStartTime || null,
            pickupEndTime: data.pickupEndTime || null,
            deliveryDate: data.deliveryDate ? moment.utc(data.deliveryDate).format('YYYY-MM-DD 00:00:00') : null,
            deliverySensitivity: data.deliverySensitivity,
            deliveryEndDate: data.deliveryEndDate ? moment.utc(data.deliveryEndDate).format('YYYY-MM-DD 00:00:00') : null,
            deliveryWindowType: data.deliveryWindowType,
            deliveryStartTime: data.deliveryStartTime || null,
            deliveryEndTime: data.deliveryEndTime || null,
            shipperId: data.shipperId,
            consigneeId: data.consigneeId,

            // Shipper
            shipperCompanyName: data.shipperCompanyName,
            shipperFullAddress: data.shipperFullAddress,
            shipperAddress1: data.shipperAddress1,
            shipperStreetNumber: data.shipperStreetNumber,
            shipperCity: data.shipperCity,
            shipperState: data.shipperState,
            shipperCountry: data.shipperCountry,
            shipperPostal: data.shipperPostal,
            shipperLatitude: data.shipperLatitude,
            shipperLongitude: data.shipperLongitude,
            shipperStateCode: data.shipperStateCode,
            shipperCountryCode: data.shipperCountryCode,

            // Consignee
            consigneeFullAddress: data.consigneeFullAddress,
            consigneeAddress1: data.consigneeAddress1,
            consigneeStreetNumber: data.consigneeStreetNumber,
            consigneeCity: data.consigneeCity,
            consigneeState: data.consigneeState,
            consigneeCountry: data.consigneeCountry,
            consigneePostal: data.consigneePostal,
            consigneeLatitude: data.consigneeLatitude,
            consigneeLongitude: data.consigneeLongitude,
            consigneeStateCode: data.consigneeStateCode,
            consigneeCountryCode: data.consigneeCountryCode,
            consigneeCompanyName: data.consigneeCompanyName,

            // Classification and equipment types
            classification: data.classification,
            linearFootage: data.linearFootage,
            totalWeight: data.totalWeight,

            // Insurance and other details — clear fields when insurance is disabled,
            // matching the reference implementation to prevent stale "0" values being saved to DB
            insuranceCurrencyType: data.insuranceCurrencyType,
            insuranceCommercialValue: data.isLoadInsuranceEnabled ? data.insuranceCommercialValue : null,
            insuranceCommercialValueUSD: data.isLoadInsuranceEnabled ? data.insuranceCommercialValueUSD : null,
            insuranceCommodity: data.insuranceCommodity?.length > 0 ? data.insuranceCommodity.join('|') : null,
            isLoadInsuranceEnabled: data.isLoadInsuranceEnabled,
            isFromQuoteRequest: true,
            isFromEditQuoteDetail: true,

            primaryEquipmentType: data.primaryEquipmentType,
            primaryEquipmentTypeFullName: data.primaryEquipmentTypeFullName,
            secondaryEquipmentTypes: data.secondaryEquipmentTypes?.join(','),
            secondaryEquipmentTypeFullNames: data.secondaryEquipmentTypeFullNames?.join(','),
            interModalType: INTER_MODAL_TYPE_EQUIPMENT_TYPES.some((type) => data.primaryEquipmentTypeFullName?.toLowerCase().includes(type)) ? data.interModalType : null,
            specialNotes: data.specialNotes || null,
        },

        services: {
            serviceId: data.serviceId?.join(','),
            //limited access
            limitedAccess: data.limitedAccess,
            ...(data.limitedAccess && {
                limitedAccessType: data.limitedAccessType,
                limitedAccessOther: data.limitedAccessOther,
            }),

            // inbond
            inbond: data.inbond,
            ...(data.inbond && {
                inbondType: data.inbondType,
                inbondName: data.inbondName,
                inbondAddress: data.inbondAddress,
                inbondContactMethod: data.inbondContactMethod,
                inbondContactValue: data.inbondContactValue,
            }),

            // dangerous goods
            dangerousGoods: data.dangerousGoods,
            ...(data.dangerousGoods && {
                dangerGoodsType: data.dangerGoodsType,
                unitedNationsNumber: data.unitedNationsNumber,
                packagingGroup: data.packagingGroup,
                goodsClass: data.goodsClass,
                description: data.description,
                emergencyContactName: data.emergencyContactName,
                emergencyContactNumber: data.emergencyContactNumber,
                emergencyContactExt: data.emergencyContactExt,
                emergencyEapnbr: data.emergencyEapnbr,
            }),
        },

        dimensions: (() => {
            if (!data.dimensions || data.dimensions.length === 0 || data.classification === QUOTE_CLASSIFICATION?.FTL) {
                return [];
            }

            if (data.dimensions.length === 1) {
                const dimension = data.dimensions[0];
                const hasValidValue =
                    (dimension.weight && dimension.weight >= 1) ||
                    (dimension.itemLength && dimension.itemLength >= 1) ||
                    (dimension.width && dimension.width >= 1) ||
                    (dimension.height && dimension.height >= 1) ||
                    (dimension.totalWeight && dimension.totalWeight >= 1);

                if (!hasValidValue) {
                    return [];
                }
            }

            return data.dimensions.map((dimension) => ({
                id: dimension.id,
                handlingUnit: dimension.handlingUnit,
                handlingUnitNo: dimension.handlingUnitNo,
                weight: dimension.weight,
                itemLength: dimension.itemLength,
                width: dimension.width,
                height: dimension.height,
                totalWeight: dimension.totalWeight,
                isStack: dimension.isStack,
                weightMeasurement: data.weightMeasurement,
                lengthMeasurement: data.lengthMeasurement,
                commodity: dimension?.commodity || null,
                freightClass: dimension.freightClass,
                itemClassForDayross: dimension.itemClassForDayross,
                // Task 37 (client decision) — multi-stop item re-routing in Edit Quote, matching
                // the Add Quote wizard's Dimensions step. null/absent on classic quotes and on any
                // stop-count-2 caller of this shared payload builder — ignored there.
                pickupStopPosition: dimension?.pickupStopPosition ?? null,
                dropStopPosition: dimension?.dropStopPosition ?? null,
            }));
        })(),
    };

    return payload;
};
